import type { SessionType } from './SessionType';

export const advisorUsageEventNames = ['advisor_active_interval'] as const;

export type AdvisorUsageEventName = (typeof advisorUsageEventNames)[number];

export const ADVISOR_USAGE_EVENT_SCHEMA_VERSION = 1 as const;
export const ADVISOR_USAGE_EVENT_SOURCE = 'authenticated_queue' as const;
export const MAX_ADVISOR_ACTIVE_INTERVAL_SECONDS = 60;

export function isAdvisorUsageEventName(value: unknown): value is AdvisorUsageEventName {
  return typeof value === 'string' && (advisorUsageEventNames as readonly string[]).includes(value);
}

export interface AdvisorUsageEventRequest {
  eventId: string;
  eventName: AdvisorUsageEventName;
  occurredAt?: string | Date;
  activeSeconds?: number;
}

export interface AdvisorUsageEventEnvelope {
  schemaVersion: typeof ADVISOR_USAGE_EVENT_SCHEMA_VERSION;
  eventId: string;
  eventName: AdvisorUsageEventName;
  advisorId: string;
  occurredAt: string;
  activeSeconds: number;
}

export interface AdvisorUsageSession {
  sessionType: SessionType;
  advisor: Pick<Advisor, 'advisorId'> | null;
}

export type NylMetricsGranularity = 'day' | 'week' | 'month';

export interface NylMetricsDateRange {
  from: string;
  to: string;
  timezone: 'America/New_York';
  allTime: boolean;
  granularity: NylMetricsGranularity;
}

export interface NylPilotMetricTotals {
  newEnrollments: number;
  agentsWithFirstUse: number;
  averageTimeToFirstUseSeconds: number | null;
  medianTimeToFirstUseSeconds: number | null;
  activeSeconds: number;
  averageActiveSeconds: number;
  invitationsSent: number;
  questionnairesCompleted: number;
  planningOutputsGenerated: number;
  policiesAdded: number;
}

export interface NylPilotAgentMetrics {
  advisorId: string;
  advisorName: string;
  geoOffice: string | null;
  enrolledAt: string | null;
  firstUseAt: string | null;
  timeToFirstUseSeconds: number | null;
  logins: number;
  activeDays: number;
  activeSeconds: number;
  invitationsSent: number;
  questionnairesCompleted: number;
  planningOutputsGenerated: number;
  policiesAdded: number;
}

export interface NylPilotTrendMetrics {
  periodStart: string;
  registeredAdvisors: number;
  firstUseAgents: number;
  advisorLogins: number;
  activeDays: number;
  activeSeconds: number;
  invitationsSent: number;
  questionnairesCompleted: number;
  planningOutputsGenerated: number;
  policiesAdded: number;
}

export interface NylPilotMetricsResponse {
  range: NylMetricsDateRange;
  overview: NylPilotMetricTotals;
  agents: NylPilotAgentMetrics[];
  trend: NylPilotTrendMetrics[];
  dataQuality: {
    agentsWithoutFirstUse: number;
    activePoliciesMissingAddedAt: number;
    usageTrackingStartedAt: string | null;
    usageTrackingIsProspective: true;
  };
}

export type NylPilotMetricsExportStatus =
  | 'pending'
  | 'processing'
  | 'retrying'
  | 'completed'
  | 'failed';

export interface NylPilotMetricsExportStartResponse {
  accepted: true;
  exportId: string;
  status: 'pending';
}

export interface NylPilotMetricsExportRequest {
  from?: string;
  to?: string;
  advisorId?: string;
  geoOffice?: string;
  scope?: 'nyl';
}

export interface NylPilotMetricsExportResponse {
  exportId: string;
  status: NylPilotMetricsExportStatus;
  createdAt: string;
  completedAt?: string;
  fileName: string;
  contentType: 'text/csv';
  rowCount?: number;
  csv?: string;
  errorMessage?: string;
}
